import {
    faArrowsAltH,
    faBars,
    faBox,
    faBrain,
    faBug,
    faCamera,
    faChevronLeft,
    faCog,
    faFilm,
    faMapPin,
    faObjectUngroup,
    faRuler,
    faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { faArrowCircleDown, faArrowCircleUp, faClock, faCompass, faSearchPlus, faWindowRestore } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tippy from "@tippyjs/react";
import { notification, Popconfirm } from "antd";
import SidekickPopoutContent from "components/SidekickPopoutContent";
import { ReactComponent as OhlSvg } from "icons/ohl.svg";
import _ from "lodash";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    flipInspectionRails,
    getNearbyOleStructures,
    getNetworkTrackResponsibility,
    resetInspectionDetectionsExportIndexRange,
    setAgreeToUsingWithDiscretion,
    setDirectionArrow,
    setDisplayExtraLayers,
    setIsMagnifyToggled,
    toggleAutoDetailCameraOption,
    toggleCompareSessionsDraggable,
    toggleDetectionsOpen,
    toggleInspectionAnnotationMode,
    toggleInspectionDetectionsExport,
    toggleInspectionMeasureMode,
    toggleInspectionSnapshot,
    toggleSettingsOpen,
    toggleSuspectViewerOpen,
} from "redux/actions";
import { currentDashboardSelector } from "redux/selectors";
import { roundArrow } from "tippy.js";
import "tippy.js/dist/border.css";
import "tippy.js/dist/svg-arrow.css";
import ELRMileAndChain from "../../util/ELRMileAndChain";
import { ReactComponent as DetailRailIcon } from "./RailButtons/DetailRailIcon.svg";
import { ReactComponent as CordelIcon } from "../../../icons/cordel.svg";
import moment from "moment";
import OBCSpinner from "components/util/OBC";
import { CHAINS_PER_MILE, METRES_PER_CHAIN, YARDS_PER_CHAIN } from "components/util/distance";

const magnifyToggledSelector = (state) => state.railInspection.magnifyToggled;
const compareOpenSelector = (state) => state.railInspection.sessionHistoryDraggable;
const inspectionSnapshotOpenSelector = (state) => state.railInspection.snapshot.open;
const displayExtraLayersSelector = (state) => state.railInspection.displayExtraLayers;
const annotateModeEnabledSelector = (state) => state.railInspection.annotations.modeEnabled;
const settingsOpenSelector = (state) => state.railInspection.settingsOpen;
const userRoleSelector = (state) => state.userDetails.userConfig.super_admin;
const sessionSelector = (state) => _.get(state.sessions, [state.playlist.data.routeID], []);
const railsFlippedSelector = (state) => state.railInspection.flipRails;
const autoScrollActiveSelector = (state) => state.railInspection.autoScrollActive;
const selectedRailImageIndexSelector = (state) => state.railInspection.selectedRailInspectionImage.index;
const elrUnitsSelector = (state) => state.userDetails.userConfig.elr_units;
const viewsSelector = (state) => state.views;
const userDetailsSelector = (state) => state.userDetails;
const railDataObjectSelector = (state) => state.railInspection.railInspectionImages.data;
const railDataLoadedSelector = (state) => state.railInspection.railInspectionImages.loadingInfo.loaded;
const railImageConfigObjectSelector = (state) => state.railInspection.railInspectionImageConfig;
const detectionsExportOpenSelector = (state) => state.railInspection.detectionsExportOpen;
const measureModeEnabledSelector = (state) => state.railInspection.measure.enabled;
const adjustmentsAppliedSelector = (state) => state.railInspection.imageAdjustmentsApplied;
const imageAdjustmentsTouchedSelector = (state) => _.get(state.railInspection.imageAdjustments, "touched");
const sperryWindowOpenSelector = (state) => state.railInspection.suspectViewerOpen;
const routePositionSelector = (state) => state.playlist.position.routePosition;
const agreedToUsingWithDiscretionSelector = (state) => state.railInspection.agreedToUsingWithDiscretion;
const coordinatesSelector = (state) => state.playlist.position.coords;
const inspectionImagesSourceSelector = (state) => _.get(state.sessions, [state.playlist.data.routeID, "inspection_images_source"], null);

const workspaceAssetGroupsSelector = (state) => {
    const currentDashboard = _.find(state.dashboards, (dash) => dash.access_token === state.access_token);
    return _.get(currentDashboard, ["config", "asset_groups"], []);
};

const RAIL_COMPARE_KEY = "rail_inspection_compare_tool_enabled";
const MEASURE_ENABLED_KEY = "inspection_measure_enabled";

const ToolTip = ({ text, children, active }) => {
    const [show, setShow] = useState(false);
    const childWithProps = React.cloneElement(children, {
        onMouseEnter: () => setShow(true),
        onMouseLeave: () => setShow(false),
    });

    if (!active) {
        return children;
    }
    return (
        <div className="Tooltip">
            {childWithProps}
            <span className={`text ${!show ? "hidden" : null}`}>
                <div className="arrow"></div>
                {text}
            </span>
        </div>
    );
};

const RailInspectToolbar = ({
    toggleMarkerTool,
    markerToolActive,
    toggleFindModalVisibility,
    videoless,
    toggleArchiveInspectionSessionModal,
    toggleRestoreArchivedInspectionSessionModal,
    showReportProblemDialog,
    navbarCollapsed,
    setNavbarCollapsed,
    toggleSourceModal,
    sourceModalOpen,
    assetNavigationWindowOpen,
    setBookmarksAndAnnotationsWindowOpen,
    bookmarksAndAnnotationsWindowOpen,
    toolbarWrapperRef,
}) => {
    const [collapsed, setCollapsed] = useState(false);
    const [buttonsCollapsed, setButtonsCollapsed] = useState(false);
    const [responsibleAreas, setResponsibleAreas] = useState(null);
    const [popConfirmVisibility, setPopConfirmVisibility] = useState(false);
    const [nearbyStructures, setNearbyStructures] = useState(null);
    const [isLoadingNearbyStructures, setIsLoadingNearbyStructures] = useState(false);

    const toolBarRef = useRef(null);
    const prevWidthRef = useRef(null);
    const dispatch = useDispatch();

    const inspectionSnapshotOpen = useSelector(inspectionSnapshotOpenSelector);
    const railImageConfigObject = useSelector(railImageConfigObjectSelector);
    const displayExtraLayers = useSelector(displayExtraLayersSelector);
    const annotateModeEnabled = useSelector(annotateModeEnabledSelector);
    const settingsOpen = useSelector(settingsOpenSelector);
    const userIsSuperAdmin = useSelector(userRoleSelector);
    const session = useSelector(sessionSelector);
    const railsFlippedValue = useSelector(railsFlippedSelector);
    const elrUnits = useSelector(elrUnitsSelector);
    const views = useSelector(viewsSelector);
    const userDetails = useSelector(userDetailsSelector);
    const railDataObject = useSelector(railDataObjectSelector);
    const currentDashboard = useSelector(currentDashboardSelector);
    const userViewOffsets = _.get(views, [userDetails.userConfig.view_id, "datum_offsets"], []);
    const workspaceViewOffsets = _.get(views, [_.get(currentDashboard, ["config", "view_id"], -1), "datum_offsets"], []);
    const datumOffsets = workspaceViewOffsets.length ? workspaceViewOffsets : userViewOffsets;
    const railDataLoaded = useSelector(railDataLoadedSelector);
    const selectedRailImageIndex = useSelector(selectedRailImageIndexSelector);
    const autoScrollActive = useSelector(autoScrollActiveSelector);
    const detectionsExportOpen = useSelector(detectionsExportOpenSelector);
    const measureModeEnabled = useSelector(measureModeEnabledSelector);
    const compareDraggableOpen = useSelector(compareOpenSelector);
    const routePosition = useSelector(routePositionSelector);
    const workspaceAssetGroups = useSelector(workspaceAssetGroupsSelector);

    const magnifyToggled = useSelector(magnifyToggledSelector);

    const adjustmentsApplied = useSelector(adjustmentsAppliedSelector);
    const imageAdjustmentsTouched = useSelector(imageAdjustmentsTouchedSelector);

    const sperryWindowOpen = useSelector(sperryWindowOpenSelector);

    const agreedToUsingWithDiscretion = useSelector(agreedToUsingWithDiscretionSelector);
    const currentCoordinates = useSelector(coordinatesSelector);
    const inspectionImagesSource = useSelector(inspectionImagesSourceSelector);

    const [imageAdjustmentTippyVisible, setImageAdjustmentTippyVisible] = useState(false);
    const handleToggleMagnify = useCallback(() => {
        dispatch(setIsMagnifyToggled(!magnifyToggled));
    }, [dispatch, magnifyToggled]);

    const toggleCompareDraggable = useCallback(() => {
        dispatch(toggleCompareSessionsDraggable(compareDraggableOpen ? false : true));
    }, [compareDraggableOpen, dispatch]);

    useEffect(() => {
        return () => {
            if (!compareDraggableOpen) return;

            dispatch(toggleCompareSessionsDraggable(false));
        };
    }, [compareDraggableOpen, dispatch]);

    useEffect(() => {
        if (responsibleAreas !== null) {
            setResponsibleAreas(null);
        }

        if (nearbyStructures !== null) {
            setNearbyStructures(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [routePosition]);

    const railImages = useMemo(() => {
        let retVal;
        if (railDataObject && railDataLoaded) {
            retVal = railDataObject; //Find first device in data - will need changing to support multiple devices
        } else {
            retVal = [];
        }
        return retVal;
    }, [railDataObject, railDataLoaded]);

    const railImage = useMemo(() => {
        if (railImages && railImages.length) {
            return railImages[selectedRailImageIndex];
        }
    }, [railImages, selectedRailImageIndex]);

    const mwvData = useMemo(() => {
        if (railImages && railImage) {
            let mwv = railImage["mwv"];
            if (mwv && mwv.elr && mwv.mile && mwv.trid && mwv.yard) {
                mwv = ELRMileAndChain.from_fields(
                    "ELR Mile & Chain",
                    "elr_mile_yards",
                    {
                        ELR: mwv.elr,
                        MILE: mwv.mile,
                        TRACK: mwv.trid,
                        YARDS: mwv.yard,
                    },
                    datumOffsets,
                    true,
                );
                return {
                    string: mwv.to_string(elrUnits, datumOffsets, true),
                    route: mwv.route,
                    track: mwv.track,
                    position: mwv.position,
                };
            } else {
                return null;
            }
        } else {
            return null;
        }
    }, [railImage, railImages, datumOffsets, elrUnits]);

    useEffect(() => {
        const collapsedValue = window.localStorage.getItem("toolbarCollapsed") === "true";

        setCollapsed(collapsedValue);
        setButtonsCollapsed(collapsedValue);
        setNavbarCollapsed(collapsedValue);
    }, [setNavbarCollapsed]);

    const allowRailFlip = useMemo(() => {
        return railImageConfigObject.horizontal;
    }, [railImageConfigObject.horizontal]);

    const railHistoryEnabled = useMemo(
        () => _.get(currentDashboard, ["config", RAIL_COMPARE_KEY], false) && mwvData && !allowRailFlip,
        [allowRailFlip, currentDashboard, mwvData],
    );

    const measureEnabled = useMemo(
        () => _.get(currentDashboard, ["config", MEASURE_ENABLED_KEY], false) && inspectionImagesSource !== "hitachi",
        [currentDashboard, inspectionImagesSource],
    );

    const toggleSnapshot = () => {
        dispatch(toggleInspectionSnapshot(!inspectionSnapshotOpen));
    };

    const hasDetailImages = useMemo(() => {
        return _.get(railImageConfigObject, "inspection_images", false)
            ? !!railImageConfigObject.inspection_images.filter((config) => config.showInModal).length
            : false;
    }, [railImageConfigObject]);

    const toggleAnnotationMode = () => {
        dispatch(toggleInspectionAnnotationMode(!annotateModeEnabled));
    };

    const toggleDetections = () => {
        if (detectionsExportOpen) {
            dispatch(toggleInspectionDetectionsExport());
            dispatch(resetInspectionDetectionsExportIndexRange());
        }
        dispatch(toggleDetectionsOpen());
    };

    const toggleContent = () => {
        setBookmarksAndAnnotationsWindowOpen(!bookmarksAndAnnotationsWindowOpen);
    };

    const toggleSperrySuspects = () => {
        dispatch(toggleSuspectViewerOpen());
    };

    const toggleSettings = () => {
        dispatch(toggleSettingsOpen());
    };

    useEffect(() => {
        const handleTransitionEnd = () => {
            const currentWidth = toolBarRef.current.offsetWidth;
            if (prevWidthRef.current < currentWidth) {
                setButtonsCollapsed(false);
            }
            prevWidthRef.current = currentWidth;
        };

        const currentElement = toolBarRef.current;
        if (currentElement) {
            currentElement.addEventListener("transitionend", handleTransitionEnd);
            prevWidthRef.current = currentElement.offsetWidth;
        }

        return () => {
            if (!currentElement) return;
            currentElement.removeEventListener("transitionend", handleTransitionEnd);
        };
    }, []);

    const formattedResponsibleAreas = useMemo(() => {
        if (!_.isObject(responsibleAreas)) {
            return null;
        }

        return (
            <div className="ResponsibleAreaTooltip">
                {Object.entries(responsibleAreas).map(([label, value]) => {
                    return (
                        <span>
                            <span className="ResponsibleAreaTooltip-Label">{label}</span>: {value}
                        </span>
                    );
                })}
            </div>
        );
    }, [responsibleAreas]);

    const formattedNearbyStructures = useMemo(() => {
        if (nearbyStructures === null) return;

        if (nearbyStructures && nearbyStructures.length === 0) {
            return <span>No nearby OLE structures found.</span>;
        }

        const formatLineReference = (asset) => {
            let yards = Math.floor((asset.position - Math.floor(asset.position / CHAINS_PER_MILE) * CHAINS_PER_MILE) * YARDS_PER_CHAIN);
            return `${asset.route_id} ${Math.floor(asset.position / CHAINS_PER_MILE)}m ${yards}y ${asset.subposition}`;
        };

        return (
            <div className="NearbyOLEAssetsContainer">
                {nearbyStructures.map((item, index) => (
                    <React.Fragment key={item.asset.asset_id}>
                        <div className="NearbyOLEAsset">
                            <FontAwesomeIcon
                                icon={item.screenDirection === "up" ? faArrowCircleUp : faArrowCircleDown}
                                size="2x"
                                opacity="0.5"
                            />
                            <div className="AssetInfo">
                                <div className="AssetInfoRow">
                                    <span className="NearbyAssetsContentLabel">ID</span>
                                    <span className="NearbyAssetsContentValue">{item.asset.landmark_id.replace(/,/g, "/")}</span>
                                </div>
                                <div className="AssetInfoRow">
                                    <span className="NearbyAssetsContentLabel">Distance</span>
                                    <span className="NearbyAssetsContentValue">
                                        {Math.floor(Math.abs(item.asset.position - routePosition.position) * METRES_PER_CHAIN)}m {item.relation}
                                    </span>
                                </div>
                                <div className="AssetInfoRow">
                                    <span className="NearbyAssetsContentLabel">Line reference</span>
                                    <span className="NearbyAssetsContentValue">{formatLineReference(item.asset)}</span>
                                </div>
                            </div>
                        </div>
                        {index === 0 && nearbyStructures.length > 1 && <hr />}
                    </React.Fragment>
                ))}
            </div>
        );
    }, [nearbyStructures, routePosition]);

    if (!window.location.href.includes("rail-inspect")) {
        return null;
    }

    const setToolBarStateInLocalStorage = (collapsed) => {
        window.localStorage.setItem("toolbarCollapsed", collapsed);
    };

    const handlePopConfirmVisibility = (visible) => {
        if (agreedToUsingWithDiscretion) {
            setPopConfirmVisibility(false);
        } else {
            setPopConfirmVisibility(visible);
        }
    };

    const cordelUrl = useMemo(() => {
        if (!session) {
            return null;
        }

        const lat = _.get(currentCoordinates, [1], null);
        const long = _.get(currentCoordinates, [0], null);
        const sessionDate = _.get(session, "first_seen", null);

        if (lat === null || long === null || sessionDate === null) {
            return null;
        }

        const date = moment.unix(sessionDate);
        const minDate = date.clone().subtract(6, "months").toISOString();
        const maxDate = date.clone().add(6, "months").toISOString();

        return `https://networkrail.cordel.ai/explorer?latitude=${lat}&longitude=${long}&radiusMetres=50&minDateUtc=${minDate}&maxDateUtc=${maxDate}`;
    }, [session, currentCoordinates]);

    const handleCordelClick = () => {
        if (cordelUrl) {
            window.open(cordelUrl, "_blank");
        }
    };

    return (
        <div
            className="ToolbarMainWrapper"
            ref={toolbarWrapperRef}>
            <div
                className={!collapsed ? "Toolbar" : "Toolbar collapsed"}
                ref={toolBarRef}>
                <div className="ToolGroup">
                    {hasDetailImages ? (
                        <Tippy
                            content={"Show detailed rail images"}
                            placement="right"
                            theme="inspection-ui"
                            arrow={roundArrow + roundArrow}
                            disabled={!collapsed}>
                            <button
                                onClick={() => {
                                    dispatch(setDisplayExtraLayers(!displayExtraLayers));
                                    dispatch(setDirectionArrow(true));
                                    dispatch(toggleAutoDetailCameraOption(false));
                                }}
                                id="DetailedRailButton"
                                className={
                                    !collapsed
                                        ? displayExtraLayers
                                            ? "ToolbarButton open active"
                                            : "ToolbarButton open"
                                        : displayExtraLayers
                                          ? "ToolbarButton active"
                                          : "ToolbarButton"
                                }>
                                <div className="ButtonIcon">
                                    <DetailRailIcon />
                                </div>
                                <span className={buttonsCollapsed ? "ButtonText closed" : "ButtonText open"}>Detailed rail images</span>
                            </button>
                        </Tippy>
                    ) : null}

                    {_.get(currentDashboard, ["config", "detections_enabled"]) && (
                        <Tippy
                            content={"Detections"}
                            placement="right"
                            theme="inspection-ui"
                            arrow={roundArrow + roundArrow}
                            disabled={!collapsed}>
                            <button
                                onClick={() => {
                                    toggleDetections();
                                }}
                                className={
                                    !collapsed
                                        ? assetNavigationWindowOpen
                                            ? "ToolbarButton open active"
                                            : "ToolbarButton open"
                                        : assetNavigationWindowOpen
                                          ? "ToolbarButton active"
                                          : "ToolbarButton"
                                }>
                                <div className="ButtonIcon">
                                    <FontAwesomeIcon icon={faBrain} />
                                </div>
                                <span className={buttonsCollapsed ? "ButtonText closed" : "ButtonText open"}>Detections</span>
                            </button>
                        </Tippy>
                    )}

                    {railHistoryEnabled && (
                        <Tippy
                            content={"Inspection history"}
                            placement="right"
                            theme="inspection-ui"
                            arrow={roundArrow + roundArrow}
                            disabled={!collapsed}>
                            <button
                                id="railInspectionCompareButton"
                                onClick={() => {
                                    toggleCompareDraggable();
                                }}
                                className={
                                    !collapsed
                                        ? compareDraggableOpen
                                            ? "ToolbarButton open active"
                                            : "ToolbarButton open"
                                        : compareDraggableOpen
                                          ? "ToolbarButton active"
                                          : "ToolbarButton"
                                }
                                disabled={autoScrollActive}>
                                <div className="ButtonIcon">
                                    <FontAwesomeIcon
                                        className="mapSearchButton-icon"
                                        icon={faClock}
                                    />
                                </div>
                                <span className={buttonsCollapsed ? "ButtonText closed" : "ButtonText open"}>Inspection History</span>
                            </button>
                        </Tippy>
                    )}

                    <Tippy
                        content={"Take a snapshot"}
                        placement="right"
                        theme="inspection-ui"
                        arrow={roundArrow + roundArrow}
                        disabled={!collapsed}>
                        <button
                            id="railInspectionSnapshotButton"
                            onClick={toggleSnapshot}
                            className={!collapsed ? "ToolbarButton open" : "ToolbarButton"}>
                            <div className="ButtonIcon">
                                <FontAwesomeIcon icon={faCamera} />
                            </div>
                            <span className={buttonsCollapsed ? "ButtonText closed" : "ButtonText open"}>Take a snapshot</span>
                        </button>
                    </Tippy>

                    <Tippy
                        content={"Place a bookmark"}
                        placement="right"
                        theme="inspection-ui"
                        arrow={roundArrow + roundArrow}
                        disabled={!collapsed}>
                        <button
                            onClick={() => {
                                toggleMarkerTool();
                            }}
                            className={
                                !collapsed
                                    ? markerToolActive
                                        ? "ToolbarButton open active"
                                        : "ToolbarButton open"
                                    : markerToolActive
                                      ? "ToolbarButton active"
                                      : "ToolbarButton"
                            }>
                            <div className="ButtonIcon">
                                <FontAwesomeIcon icon={faMapPin} />
                            </div>
                            <span className={buttonsCollapsed ? "ButtonText closed" : "ButtonText open"}>Place a bookmark</span>
                        </button>
                    </Tippy>

                    <Tippy
                        content={"Annotate"}
                        placement="right"
                        theme="inspection-ui"
                        arrow={roundArrow + roundArrow}
                        disabled={!collapsed}>
                        <button
                            onClick={() => {
                                toggleAnnotationMode(!annotateModeEnabled);
                            }}
                            className={
                                !collapsed
                                    ? annotateModeEnabled
                                        ? "ToolbarButton open active"
                                        : "ToolbarButton open"
                                    : annotateModeEnabled
                                      ? "ToolbarButton active"
                                      : "ToolbarButton"
                            }>
                            <div className="ButtonIcon">
                                <FontAwesomeIcon icon={faObjectUngroup} />
                            </div>
                            <span className={buttonsCollapsed ? "ButtonText closed" : "ButtonText open"}>Annotate</span>
                        </button>
                    </Tippy>

                    {measureEnabled && (
                        <Popconfirm
                            placement="right"
                            title={"Measurements provided by this tool are approximate and should be used with discretion"}
                            onConfirm={() => {
                                dispatch(setAgreeToUsingWithDiscretion());
                                if (!measureModeEnabled) dispatch(setIsMagnifyToggled(false));
                                dispatch(toggleInspectionMeasureMode(!measureModeEnabled));
                            }}
                            onVisibleChange={handlePopConfirmVisibility}
                            visible={popConfirmVisibility}
                            okText="Confirm"
                            overlayClassName="hide-cancel-button">
                            <button
                                onClick={() => {
                                    if (agreedToUsingWithDiscretion) {
                                        if (!measureModeEnabled) dispatch(setIsMagnifyToggled(false));
                                        dispatch(toggleInspectionMeasureMode(!measureModeEnabled));
                                    }
                                }}
                                className={
                                    !collapsed
                                        ? measureModeEnabled
                                            ? "ToolbarButton open active"
                                            : "ToolbarButton open"
                                        : measureModeEnabled
                                          ? "ToolbarButton active"
                                          : "ToolbarButton"
                                }>
                                <div className="ButtonIcon">
                                    <FontAwesomeIcon icon={faRuler} />
                                </div>
                                <span className={buttonsCollapsed ? "ButtonText closed" : "ButtonText open"}>Measure</span>
                            </button>
                        </Popconfirm>
                    )}

                    <Tippy
                        content={"Content"}
                        placement="right"
                        theme="inspection-ui"
                        arrow={roundArrow + roundArrow}
                        disabled={!collapsed}>
                        <button
                            onClick={toggleContent}
                            className={
                                !collapsed
                                    ? bookmarksAndAnnotationsWindowOpen
                                        ? "ToolbarButton open active"
                                        : "ToolbarButton open"
                                    : bookmarksAndAnnotationsWindowOpen
                                      ? "ToolbarButton active"
                                      : "ToolbarButton"
                            }>
                            <div className="ButtonIcon">
                                <FontAwesomeIcon icon={faBars} />
                            </div>
                            <span className={buttonsCollapsed ? "ButtonText closed" : "ButtonText open"}>Content</span>
                        </button>
                    </Tippy>

                    <Tippy
                        content={"Image adjustments"}
                        placement="right"
                        theme="inspection-ui"
                        arrow={roundArrow + roundArrow}
                        visible={imageAdjustmentTippyVisible}
                        onClickOutside={() => setImageAdjustmentTippyVisible(false)}
                        disabled={!collapsed}>
                        <button
                            id="InspectionImageAdjustmentsButton"
                            onClick={() => {
                                toggleSettings();
                            }}
                            onMouseEnter={() => setImageAdjustmentTippyVisible(true)}
                            onMouseLeave={() => setImageAdjustmentTippyVisible(false)}
                            className={
                                !collapsed
                                    ? settingsOpen
                                        ? "ToolbarButton open active"
                                        : "ToolbarButton open"
                                    : settingsOpen
                                      ? "ToolbarButton active"
                                      : "ToolbarButton"
                            }>
                            {adjustmentsApplied ? (
                                <Tippy
                                    content={imageAdjustmentsTouched ? "Image adjustments applied" : "Automatic image adjustments applied"}
                                    theme="inspection-ui"
                                    arrow={roundArrow + roundArrow}
                                    onShow={() => setImageAdjustmentTippyVisible(false)}>
                                    <div className="ToolbarButtonBadge"></div>
                                </Tippy>
                            ) : null}

                            <div className="ButtonIcon">
                                <FontAwesomeIcon icon={faCog} />
                            </div>
                            <span className={buttonsCollapsed ? "ButtonText closed" : "ButtonText open"}>Image adjustment</span>
                        </button>
                    </Tippy>

                    <Tippy
                        content={<SidekickPopoutContent />}
                        placement="right"
                        theme="inspection-ui"
                        arrow={roundArrow + roundArrow}
                        interactive={true}
                        appendTo={document.body}>
                        <button className={collapsed ? "ToolbarButton" : "ToolbarButton open"}>
                            <div className="ButtonIcon">
                                <FontAwesomeIcon icon={faWindowRestore} />
                            </div>
                            <span className={buttonsCollapsed ? "ButtonText closed" : "ButtonText open"}>Widgets</span>
                        </button>
                    </Tippy>

                    <Tippy
                        content={"Search for a location"}
                        placement="right"
                        theme="inspection-ui"
                        arrow={roundArrow + roundArrow}
                        disabled={!collapsed}>
                        <button
                            onClick={() => {
                                toggleFindModalVisibility(true);
                            }}
                            className={!collapsed ? "ToolbarButton open" : "ToolbarButton"}>
                            <div className="ButtonIcon">
                                <FontAwesomeIcon
                                    className="mapSearchButton-icon"
                                    icon={faSearch}
                                />
                            </div>
                            <span className={buttonsCollapsed ? "ButtonText closed" : "ButtonText open"}>Search for location</span>
                        </button>
                    </Tippy>

                    <Tippy
                        content={"More cameras"}
                        placement="right"
                        theme="inspection-ui"
                        arrow={roundArrow + roundArrow}
                        disabled={!collapsed}>
                        <button
                            onClick={() => {
                                toggleSourceModal(!sourceModalOpen);
                            }}
                            id={"MoreCamerasInspectionButton"}
                            className={!collapsed ? "ToolbarButton open" : "ToolbarButton"}>
                            <div className="ButtonIcon">
                                <FontAwesomeIcon
                                    className="mapSearchButton-icon"
                                    icon={faFilm}
                                />
                            </div>
                            <span className={buttonsCollapsed ? "ButtonText closed" : "ButtonText open"}>More cameras</span>
                        </button>
                    </Tippy>

                    <Tippy
                        content={"Sperry suspects"}
                        placement="right"
                        theme="inspection-ui"
                        arrow={roundArrow + roundArrow}
                        disabled={!collapsed}>
                        <button
                            onClick={() => {
                                toggleSperrySuspects();
                            }}
                            className={
                                !collapsed
                                    ? sperryWindowOpen
                                        ? "ToolbarButton open active"
                                        : "ToolbarButton open"
                                    : sperryWindowOpen
                                      ? "ToolbarButton active"
                                      : "ToolbarButton"
                            }
                            disabled={autoScrollActive}>
                            <div className="ButtonIcon">
                                <DetailRailIcon />
                            </div>
                            <span className={buttonsCollapsed ? "ButtonText closed" : "ButtonText open"}>Sperry viewer</span>
                        </button>
                    </Tippy>

                    <ToolTip
                        text="Magnifying glass"
                        active={collapsed}>
                        <button
                            disabled={measureModeEnabled}
                            onClick={handleToggleMagnify}
                            className={`ToolbarButton ${magnifyToggled ? "active" : ""} ${!collapsed ? "open" : ""}`}>
                            <div className="ButtonIcon">
                                <FontAwesomeIcon
                                    className="mapSearchButton-icon"
                                    icon={faSearchPlus}
                                />
                            </div>
                            <span className={buttonsCollapsed ? "ButtonText closed" : "ButtonText open"}>Magnifying glass</span>
                        </button>
                    </ToolTip>

                    {videoless && userIsSuperAdmin ? (
                        <Tippy
                            content={session.inspection_archive_status === 0 ? "Archive session" : "Restore session"}
                            placement="right"
                            theme="inspection-ui"
                            arrow={roundArrow + roundArrow}
                            disabled={!collapsed}>
                            <button
                                onClick={() => {
                                    if (session.inspection_archive_status === 0) {
                                        toggleArchiveInspectionSessionModal();
                                    } else if (session.inspection_archive_status === 1) {
                                        toggleRestoreArchivedInspectionSessionModal();
                                    }
                                }}
                                className={!collapsed ? "ToolbarButton open" : "ToolbarButton"}>
                                <div className="ButtonIcon">
                                    <FontAwesomeIcon icon={faBox} />
                                </div>
                                <span className={buttonsCollapsed ? "ButtonText closed" : "ButtonText open"}>
                                    {session.inspection_archive_status === 0 ? "Archive session" : "Restore session"}
                                </span>
                            </button>
                        </Tippy>
                    ) : null}
                    <Tippy
                        content={"Open in Cordel"}
                        placement="right"
                        theme="inspection-ui"
                        arrow={roundArrow + roundArrow}
                        disabled={!collapsed}>
                        <button
                            onClick={handleCordelClick}
                            className={!collapsed ? "ToolbarButton open" : "ToolbarButton"}
                            disabled={!cordelUrl}>
                            <div className="ButtonIcon Cordel">
                                <CordelIcon />
                            </div>
                            <span className={buttonsCollapsed ? "ButtonText closed" : "ButtonText open"}>Open in Cordel</span>
                        </button>
                    </Tippy>
                    {allowRailFlip && (
                        <Tippy
                            content={"Flip rails"}
                            placement="right"
                            theme="inspection-ui"
                            arrow={roundArrow + roundArrow}
                            disabled={!collapsed}>
                            <button
                                onClick={() => {
                                    dispatch(flipInspectionRails(!railsFlippedValue));
                                }}
                                className={
                                    !collapsed
                                        ? railsFlippedValue
                                            ? "ToolbarButton open active"
                                            : "ToolbarButton open"
                                        : railsFlippedValue
                                          ? "ToolbarButton active"
                                          : "ToolbarButton"
                                }>
                                <div className="ButtonIcon">
                                    <FontAwesomeIcon icon={faArrowsAltH} />
                                </div>
                                <span className={buttonsCollapsed ? "ButtonText closed" : "ButtonText open"}>Flip Rails</span>
                            </button>
                        </Tippy>
                    )}
                </div>
                <div className="ToolGroup">
                    {workspaceAssetGroups.includes("2") && (
                        <Tippy
                            content={
                                formattedNearbyStructures ||
                                (isLoadingNearbyStructures ? (
                                    <OBCSpinner
                                        size={24}
                                        speed={3}
                                        color={"#e8dfff"}
                                    />
                                ) : (
                                    "Nearby OLE Structures"
                                ))
                            }
                            className="NearbyOLEAssetsContainerTippy"
                            placement="right"
                            theme="inspection-ui"
                            arrow={roundArrow + roundArrow}
                            trigger={!collapsed ? "click" : "mouseenter click"}>
                            <button
                                onClick={() => {
                                    if (nearbyStructures === null) {
                                        setIsLoadingNearbyStructures(true);

                                        dispatch(
                                            getNearbyOleStructures((response) => {
                                                if (response.success) {
                                                    setNearbyStructures(response.data);
                                                } else {
                                                    notification.error({
                                                        message: "Error Loading Nearby Structures",
                                                        description: "Nearby OLE structures could not be retrieved at this time.",
                                                    });
                                                }
                                                setIsLoadingNearbyStructures(false);
                                            }),
                                        );
                                    }
                                }}
                                className={!collapsed ? "ToolbarButton open" : "ToolbarButton"}>
                                <div className="ButtonIcon Ohl">
                                    <OhlSvg style={{ width: 18, height: 18 }} />
                                </div>
                                <span className={buttonsCollapsed ? "ButtonText closed" : "ButtonText open"}>Nearby OLE Structures</span>
                            </button>
                        </Tippy>
                    )}
                    {userIsSuperAdmin && (
                        <Tippy
                            content={formattedResponsibleAreas || "Responsible Area"}
                            placement="right"
                            theme="inspection-ui"
                            arrow={roundArrow + roundArrow}
                            trigger={!collapsed ? "click" : "mouseenter click"}>
                            <button
                                onClick={() => {
                                    if (responsibleAreas === null) {
                                        dispatch(getNetworkTrackResponsibility()).then((response) => {
                                            setResponsibleAreas(response.responsible_areas);
                                        });
                                    }
                                }}
                                className={!collapsed ? "ToolbarButton open" : "ToolbarButton"}>
                                <div className="ButtonIcon">
                                    <FontAwesomeIcon icon={faCompass} />
                                </div>
                                <span className={buttonsCollapsed ? "ButtonText closed" : "ButtonText open"}>Responsible Area</span>
                            </button>
                        </Tippy>
                    )}
                    <Tippy
                        content={"Report a bug"}
                        placement="right"
                        theme="inspection-ui"
                        arrow={roundArrow + roundArrow}
                        disabled={!collapsed}>
                        <button
                            onClick={() => {
                                showReportProblemDialog();
                            }}
                            className={!collapsed ? "ToolbarButton open" : "ToolbarButton"}>
                            <div className="ButtonIcon">
                                <FontAwesomeIcon icon={faBug} />
                            </div>
                            <span className={buttonsCollapsed ? "ButtonText closed" : "ButtonText open"}>Report a bug</span>
                        </button>
                    </Tippy>
                </div>
            </div>
            <div className="CollapseButtonWrapper">
                <div
                    className="CollapseButton"
                    onClick={() => {
                        setCollapsed(!collapsed);
                        setToolBarStateInLocalStorage(!collapsed);
                        setButtonsCollapsed(!buttonsCollapsed);
                        setNavbarCollapsed(!navbarCollapsed);
                    }}>
                    <FontAwesomeIcon
                        className={!collapsed ? "close" : "open"}
                        icon={faChevronLeft}
                    />
                </div>
            </div>
        </div>
    );
};

export default RailInspectToolbar;
